import { getComputedColor } from '@/utils/color';

export const EDITABLE_CONTENT_CLASS = 'pendo-guide-editable-content';

export const EDITABLE_CONTENT_AREA_CLASS = 'pendo-guide-editable-content-area';

export const PENDO_TEXT_CLASS = 'bb-text';

export const PENDO_BUTTON_CLASS = '_pendo-button';

export const PENDO_EDITING_ID_ATTRIBUTE = 'data-editing-id';

export const POLL_ID_ATTRIBUTE = 'data-pendo-poll-id';

export const CONTENT_EDITABLE_ATTRIBUTE = 'contenteditable';

export const EDITABLE_CONTENT_TYPE = 'data-content-editable-type';

const TEXT_SELECTOR = '#pendo-guide-container > [id*="pendo-text-"], ._pendo-row > [id*="pendo-text-"]';
const BUTTON_SELECTOR = '._pendo-row ._pendo-button';

export const POLL_SELECTOR = '[class*="-poll-question"]';

export const EDITABLE_ELEMENTS_SELECTOR = `${TEXT_SELECTOR}, ${BUTTON_SELECTOR}, ${POLL_SELECTOR}`;

export const DEFAULT_STEP_Z_INDEX = 300000;

export function getElementByEditingId (editingId) {
    return document.querySelector(`[data-editing-id="${editingId}"]`);
}

export function makeElementEditable ({ element, changeHandler }) {
    const isAlreadyEditable = element.classList.contains(EDITABLE_CONTENT_CLASS);
    if (isAlreadyEditable) return;

    if (element.classList.contains(PENDO_TEXT_CLASS)) {
        return makeTextEditable({ element, changeHandler });
    }

    if (element.classList.contains(PENDO_BUTTON_CLASS)) {
        return makeButtonEditable({ element, changeHandler });
    }
}

export function convertLineBreakToParagraph (event) {
    if (event.inputType === 'insertLineBreak') {
        event.preventDefault();
        document.execCommand('insertParagraph');
    }
}

export function makeTextEditable ({ element, changeHandler }) {
    element.classList.add(EDITABLE_CONTENT_CLASS);
    element.setAttribute(CONTENT_EDITABLE_ATTRIBUTE, true);
    element.setAttribute(EDITABLE_CONTENT_TYPE, 'text');
    element.addEventListener('blur', changeHandler);
    // We convert the default line break insertion because it interacts poorly with turndown since turndown removes those new lines
    element.addEventListener('beforeinput', convertLineBreakToParagraph);
}

export function makeButtonEditable ({ element, changeHandler }) {
    element.classList.add(EDITABLE_CONTENT_CLASS);
    element.setAttribute(EDITABLE_CONTENT_TYPE, 'button');
    element.addEventListener('focusout', changeHandler);
    const {
        textContent,
        style: { padding },
    } = element;

    // We need to add a span to the button to make it play nicely with contentEditable
    const span = document.createElement('span');
    span.classList.add(EDITABLE_CONTENT_AREA_CLASS);
    span.setAttribute(CONTENT_EDITABLE_ATTRIBUTE, 'plaintext-only');
    span.setAttribute(PENDO_EDITING_ID_ATTRIBUTE, element.getAttribute(PENDO_EDITING_ID_ATTRIBUTE));
    span.textContent = textContent;
    span.style.padding = padding;
    element.textContent = '';
    element.style.padding = 'unset';
    element.appendChild(span);
}

export function getBestOutlineColor (stepBackgroundRgbString) {
    const preferredColor = '--raw-palette-blue-40';
    const colorOptions = [preferredColor, '--raw-palette-gray-0', '--raw-palette-gray-100'];

    const preferredColorRgbString = `rgb(${getComputedColor(preferredColor)})`;
    const preferredColorRgb = rgbStringToObject(preferredColorRgbString);
    const backgroundRgb = rgbStringToObject(stepBackgroundRgbString);
    const preferredContrastRatio = getContrastRatio(backgroundRgb, preferredColorRgb);
    /*
        even though a ratio of 2.3 (the contrast value of blue40 on a #FFFFFF white background) doesn't
        meet WCAG standards it is consistent with current VDS outline color and Design's preference for now
    */
    const isPreferredRatioAcceptable = preferredContrastRatio >= 2.3;

    if (isPreferredRatioAcceptable) return preferredColorRgbString;

    let bestBorderColor = colorOptions[1];
    let bestRatio = 0;

    colorOptions.shift();
    colorOptions.forEach((color) => {
        const colorRgbString = `rgb(${getComputedColor(color)})`;
        const colorRgb = rgbStringToObject(colorRgbString);
        const ratio = getContrastRatio(backgroundRgb, colorRgb);
        if (ratio > bestRatio) {
            bestRatio = ratio;
            bestBorderColor = colorRgbString;
        }
    });

    return bestBorderColor;
}

/*
    formula is from https://www.w3.org/TR/WCAG20-TECHS/G17.html#G17-tests,
    consistent with our Theme builder's WCAG recommendations
*/
function getLuminance (r, g, b) {
    const result = [r, g, b].map((value) => {
        const sRGB = value / 255;

        // eslint-disable-next-line no-restricted-properties
        return sRGB <= 0.03928 ? sRGB / 12.92 : Math.pow((sRGB + 0.055) / 1.055, 2.4);
    });

    return result[0] * 0.2126 + result[1] * 0.7152 + result[2] * 0.0722;
}

function getContrastRatio (color1, color2) {
    const lum1 = getLuminance(color1.r, color1.g, color1.b);
    const lum2 = getLuminance(color2.r, color2.g, color2.b);

    const brightest = Math.max(lum1, lum2);
    const darkest = Math.min(lum1, lum2);

    return (brightest + 0.05) / (darkest + 0.05);
}

function rgbStringToObject (rgbString) {
    const match = rgbString.match(/^rgb\((\d+),\s*(\d+),\s*(\d+)\)$/);
    if (!match) return null;

    return {
        r: parseInt(match[1], 10),
        g: parseInt(match[2], 10),
        b: parseInt(match[3], 10),
    };
}
